import React, { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { APIEndpoint } from 'common/enum';
import {
  universalGetRequest,
  universalPostRequest,
} from 'common/requestHandler';
import HeaderComp from 'components/elements/HeadingComponent';
import useNavigateWithUUID from 'hooks/useNavigateWithUUID';
import { popupNotification } from 'notifications/ToastNotification';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

import ContractDetailsDialog from './components/ContractDetailsDialog';
import ContractsTable from './components/ContractsTable';
import {
  Contract,
  ContractsResponse,
  sortContracts,
} from './contractManagement.utils';

const ContractManagement: React.FC = () => {
  const { navigateWithUuid } = useNavigateWithUUID();
  const { isSignVerifyEnabled, companyId, customerId, employeeId, username } =
    useSelector((state: RootState) => state.auth);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [balance, setBalance] = useState<string>('0.00');
  const [currency, setCurrency] = useState<string | null>(null);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);

  const getContracts = async () => {
    try {
      setLoading(true);
      const response = await universalGetRequest<ContractsResponse>(
        APIEndpoint.getSecDocsMain,
        { actionName: 'GetContracts', httpMethod: 'GET' }
      );

      if ('error' in response) {
        console.error('Error fetching contracts:', response.error);
        return;
      }

      const sortedContracts = sortContracts(response.data.contracts);
      setContracts(sortedContracts);
      setBalance(response.data.available_balance);
      setCurrency(response.data.currency);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePdfSave = (pdfBytes: Uint8Array) => {
    const blob = new Blob([pdfBytes as unknown as BlobPart], {
      type: 'application/pdf',
    });
    const url = URL.createObjectURL(blob);
    setSelectedPdf(url);
  };

  const handleCreateContract = () => {
    navigateWithUuid('contract-management/new-contract');
  };

  const handleViewContract = (contract: Contract) => {
    setSelectedContract(contract);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedContract(null);
  };

  const handleResendDocumentLink = async (
    contractId: string,
    signerUuid: string,
    signerEmail: string
  ) => {
    try {
      setResendLoading(true);

      const response = await universalPostRequest(
        APIEndpoint.resendSignDocumentEmail,
        {
          contractId,
          signerUuid,
          signerEmail,
          email: signerEmail,
        }
      );

      if ('error' in response) {
        popupNotification(response.error, false);
      } else {
        popupNotification('Document link resent successfully', true);
      }
    } catch (error) {
      popupNotification('Failed to resend document link', false);
    } finally {
      setResendLoading(false);
    }
  };
  useEffect(() => {
    getContracts();
  }, []);

  return (
    <>
      <HeaderComp>
        <Typography variant="h5" fontWeight="bold">
          Contract Management
        </Typography>
      </HeaderComp>
      {/* {isSignVerifyEnabled ? (
        <SetupContractManagement />
      ) :} ( */}
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.875rem' }}
            >
              Available Balance: {formatCurrency(balance, currency)}
            </Typography>
          </Box> */}
        </Box>

        <Box sx={{ mb: 4 }}>
          <ContractsTable
            contracts={contracts}
            onViewContract={handleViewContract}
            loading={loading}
            balance={balance}
            currency={currency}
            onCreateNewContract={handleCreateContract}
          />
        </Box>
      </Box>

      <ContractDetailsDialog
        open={dialogOpen}
        contract={selectedContract}
        onClose={handleCloseDialog}
        onResendDocumentLink={handleResendDocumentLink}
        resendLoading={resendLoading}
      />
    </>
  );
};

export default ContractManagement;
